import { getCurrentDayInTimezone, getCurrentMonthInTimezone, getMonthAccordingToBirthdayDay, getPredictionYear, getPredictionYearAccordingMonthAndDay, getPredictionYearForMonth } from "../../utils/dateFromat";
import { CategoryType, JaiminiType, MDADPredictionType, MonthEnum, NadiType, PadithiEnum, PredictionType } from "../../utils/enums";

import moment from "moment-timezone";

export const createGetPredictionPayload = (filter) => {
  const { conditionType } = filter;

  const handlers = {
    [PadithiEnum.LAL_KITAB]: buildLalKitabPayload,
    [PadithiEnum.NADI]: buildNadiPayload,
    [PadithiEnum.JAIMINI]: buildNadiPayload,
    [PadithiEnum.PARASHARA]: buildNadiPayload,
  };

  const handler = handlers[conditionType];
  return handler ? handler(filter) : buildNadiPayload(filter);
};


function buildLalKitabPayload(filter) {
  const { conditionType, predictionType, category, month, day } = filter;

  const payload = {
    conditionType,
    predictionType,
    category,
  };

  if (predictionType === PredictionType.YEAR) {
    return payload;
  }

  if (predictionType === PredictionType.MONTH) {
    return { ...payload, month };
  }

  if (predictionType === PredictionType.DAY) {
    return {
      ...payload,
      month,
      day,
      category: CategoryType.DAY
    };
  }

  return payload;
}

function buildNadiPayload(filter) {
  switch (filter.predictionType) {
    case MDADPredictionType.MD:
      return {
        conditionType: filter.conditionType,
        predictionType: PredictionType.YEAR,
        category : filter.category
      }
    case MDADPredictionType.AD:
      return {
        conditionType: filter.conditionType,
        predictionType: PredictionType.MONTH,
         category : filter.category
      }
    default:
      return {
        conditionType: filter.conditionType,
        predictionType: PredictionType.YEAR,
         category : filter.category
      }
  }

}

function buildJaiminiPayload(filter) {
  switch (filter.predictionType) {
    case JaiminiType.MAHADASHA:
      return {
        conditionType: filter.conditionType,
        predictionType: PredictionType.YEAR,
      }
    case JaiminiType.ANTRADASHA:
      return {
        conditionType: filter.conditionType,
        predictionType: PredictionType.MONTH,
      }
    default:
      return {
        conditionType: filter.conditionType,
        predictionType: PredictionType.YEAR,
      }
  }

}

function buildParasharaPayload(filter) {
  return {
    conditionType: filter.conditionType,
  };
}


export const getPredictionLevel = ({ conditionType, category, predictionType }) => {
  const handlers = {
    [PadithiEnum.LAL_KITAB]: getLalKitabPredictionLevel,
  };

  const handler = handlers[conditionType];
  return handler
    ? handler({ category, predictionType })
    : getDefaultPredictionLevel(predictionType);
};

function getDefaultPredictionLevel(predictionType) {
  switch (predictionType) {
    case PredictionType.YEAR:
    case MDADPredictionType.MD:
      return "Yearly Predictions";

    case PredictionType.MONTH:
    case MDADPredictionType.AD:
      return "Monthly Predictions";
    case PredictionType.DAY:
      return "Day";
    default:
      return "";
  }
}

function getLalKitabPredictionLevel({ category, predictionType }) {
  if (category === CategoryType.PROMISE && predictionType === PredictionType.YEAR) return "";

  switch (predictionType) {
    case PredictionType.YEAR:
      return "Yearly Predictions";
    case PredictionType.MONTH:
      return "Monthly Predictions";
    case PredictionType.DAY:
      return "Day";
    default:
      return "Yearly Predictions";
  }
}

export function createPredictionInterval({
  birthDate,
  birthTZ,
  currentTZ,
  intervalType,
  month,
  day,
}) {
  if (!birthDate) return null;

  const birth = moment.tz(birthDate, birthTZ);
  const birthMonth = birth.month() + 1;
  const birthDay = birth.date();

  // Compute correct prediction year based on selected month/day
  const year = getPredictionYearAccordingMonthAndDay({
    birthDate,
    birthTZ,
    currentTZ,
    month,
    day,
  });

  switch (intervalType) {
    // ---------------------- YEARLY ----------------------
    case "yearly": {
      const start = moment.tz(
        `${year - 1}-${birthMonth}-${birthDay}`,
        currentTZ
      );

      const end = start.clone().add(1, "year").subtract(1, "day");

      return { start: start.toISOString(), end: end.toISOString() };
    }

    // ---------------------- MONTHLY ----------------------
    case "monthly": {
      const selectedMonth = month ?? (moment.tz(currentTZ).month() + 1);

      const start = moment.tz(
        `${year}-${selectedMonth}-${birthDay}`,
        currentTZ
      );

      const end = start.clone().add(1, "month").subtract(1, "day");

      return { start: start.toISOString(), end: end.toISOString() };
    }

    // ---------------------- DAILY ----------------------
    case "daily": {
      if (!month || !day) return null;

      const start = moment.tz(`${year}-${month}-${day}`, currentTZ);
      const end = start.clone().add(1, "day");

      return { start: start.toISOString(), end: end.toISOString() };
    }

    default:
      return null;
  }
}

function getPredictionYearRange({ birthDate, birthTZ, timezone }) {

  const now = moment().tz(timezone);

  const bday = moment.tz(birthDate, birthTZ);

  const bMonth = bday.month() + 1;
  const bDay = bday.date();

  const thisYearBirthday = moment.tz(
    `${now.year()}-${bMonth}-${bDay}`,
    birthTZ
  );

  let start, end;

  if (now.isBefore(thisYearBirthday, "day")) {
    start = moment(thisYearBirthday).subtract(1, "year");
    end = thisYearBirthday;
  } else {
    start = thisYearBirthday;
    end = moment(thisYearBirthday).add(1, "year");
  }

  const finalEnd = moment(end).subtract(1, "day");

  return {
    startDate: start.toDate(),
    endDate: finalEnd.toDate(),
    startDateFormatted: start.format("DD MMM YYYY"),
    endDateFormatted: finalEnd.format("DD MMM YYYY")
  };
}

// function getPredictionMonthRange({
//   birthDate,
//   birthTZ,
//   timezone,
//   month,
// }) {
  
//   if (!month || month === MonthEnum.CURRENT_MONTH) {
//     month = getMonthAccordingToBirthdayDay(birthDate, timezone, birthTZ);
//   }

//   const todayDay = getCurrentDayInTimezone(timezone);
//   const currentMonth = getCurrentMonthInTimezone(timezone);

//   const bday = moment.tz(birthDate, birthTZ);
//   const bDay = bday.date();

//   const year = getPredictionYearForMonth({
//     birthDate,
//     birthTZ,
//     timezone,
//     month
//   });

//   // if (month === currentMonth && todayDay < bDay) {
//   //   month = month - 1;
//   //   if (month === 0) {
//   //     month = 12;
//   //   }
//   // }

//   // Build start date using birthday DAY
//   let start = moment(`${year}-${month}-${bDay}`);

//   if (start.month() !== (month - 1)) {
//     start = moment(`${year}-${month}`).endOf("month");
//   }

//   // End = next month same birthday day - 1 day
//   // let end = moment(start).add(1, "month").subtract(bDay - 1);
//   let end = moment(start).add(1, "month").subtract(bDay - 1);
  
//   if (end.date() !== (bDay - 1)) {
//     const endMOnth = (month + 1) > 12 ? 1 : (month + 1);
//     if(bDay === 1) {
//       end = moment(`${year}-${month}`).endOf("month");
//     }else {
//       end = moment(`${year}-${endMOnth}`).endOf("month").subtract(1, "day");
//     }
    
//   }

//   return {
//     startDate: start.toDate(),
//     endDate: end.toDate(),
//     startDateFormatted: start.format("DD MMM YYYY"),
//     endDateFormatted: end.format("DD MMM YYYY"),
//   };
// }

function getPredictionMonthRange({
  birthDate,
  birthTZ,
  timezone,
  month,
}) {
  if (!month || month === MonthEnum.CURRENT_MONTH) {
    month = getMonthAccordingToBirthdayDay(birthDate, timezone, birthTZ);
  }

  const todayDay = getCurrentDayInTimezone(timezone);
  const currentMonth = getCurrentMonthInTimezone(timezone);

  const bday = moment.tz(birthDate, birthTZ);
  const birthDay = bday.date();

  const year = getPredictionYearForMonth({
    birthDate,
    birthTZ,
    timezone,
    month
  });

  // -----------------------------------------
  // 1. START DATE
  // -----------------------------------------
  let start = moment.tz(
    { year, month: month - 1, day: birthDay },
    timezone
  );

  let startFallbackUsed = false;

  if (!start.isValid() || start.month() !== month - 1) {
    start = moment
      .tz({ year, month: month - 1, day: 1 }, timezone)
      .endOf("month");

    startFallbackUsed = true;
  }

  // -----------------------------------------
  // 2. END DATE
  // -----------------------------------------
  let nextMonth = start.clone().add(1, "month");
  let end = nextMonth.clone().date(birthDay);

  let endFallbackUsed = false;

  // If invalid (e.g., birthday=31 in a short month)
  if (!end.isValid() || end.month() !== nextMonth.month()) {
    endFallbackUsed = true;

    // birthday = 1 → same month end-of-month
    if (birthDay === 1) {
      end = start.clone().endOf("month");
    } else {
      end = nextMonth.clone().endOf("month");
    }
  } else {
    // Normal case subtract 1 day
    end = end.subtract(1, "day");
  }

  // -----------------------------------------
  // SPECIAL CASE: birthday 31 fallback → we want SAME last day (not minus 1)
  // -----------------------------------------
  if (birthDay === 31 && startFallbackUsed && endFallbackUsed) {
    // Overwrite: end should be the last day of next month
    end = nextMonth.clone().endOf("month");
  }

  return {
    startDate: start.toDate(),
    endDate: end.toDate(),
    startDateFormatted: start.format("DD MMM YYYY"),
    endDateFormatted: end.format("DD MMM YYYY"),
  };
}

function getPredictionDayRange({
  birthDate,
  birthTZ,
  timezone,
  month,
  day,
}) {
  if (!month) {
    month = getCurrentMonthInTimezone(timezone);
  }
  if (!day) {
    day = getCurrentDayInTimezone(timezone);
  }
  const year = getPredictionYear({ birthDate, birthTZ, timezone, month, day });

  const start = moment(`${year}-${month}-${day}`);
  const end = moment(start).add(1, "day").subtract(1, "second");

  return {
    startDate: start.toDate(),
    endDate: end.toDate(),
    startDateFormatted: start.format("DD MMM YYYY"),
    endDateFormatted: end.format("DD MMM YYYY"),
  };
}

export function getIntervalByPredictionType({
  predictionType,
  birthDate,
  birthTZ,
  currentTZ,
  month,
  day,
}) {

  switch (predictionType) {
    case PredictionType.YEAR:
      return getPredictionYearRange({ birthDate, birthTZ, timezone: currentTZ });

    case PredictionType.MONTH:
      return getPredictionMonthRange({
        birthDate,
        birthTZ,
        timezone: currentTZ,
        month,
      })

    case PredictionType.DAY:
      return getPredictionDayRange({
        birthDate,
        birthTZ,
        timezone: currentTZ,
        month,
        day
      })
    default:
      return getPredictionYearRange({ birthDate, birthTZ: birthTZ, timezone: currentTZ });

  }

}
